import type { MarketMakerRequestStatusEnumType } from '@forgd/shared/enums'

type NavItemStatus = 'none' | 'lock' | 'available' | 'completed' | 'requoted' | 'cancelled'

export interface NavItem {
  label: string
  link: string
  status: NavItemStatus
}

export const RFQ_NAV_ITEM = 'Request for Quotations'
export const ROUND_1_NAV_ITEM = 'Round 1 Received Quotes'
export const ROUND_2_NAV_ITEM = 'Round 2 Received Requotes'

/**
 * Maps the RFQ request statuses to navigation items with appropriate status indicators.
 *
 * This function returns a list of NavItems (RFQ, Round 1, Round 2) with status values
 * that reflect the latest applicable rfq state, based on the provided RFQ status history.
 *
 * - If the RFQ was cancelled, only the relevant section (based on round number) is returned with 'cancelled' status.
 * - Otherwise, each nav item's status is derived by scanning the statuses from most recent to oldest
 *   and applying the first matching mapping from `meta.statuses`.
 *
 * @param baseUrl - URL to RFQ page.
 * @param statuses - Full status history from the RFQ details response (ordered desc, default []).
 * @param round - Current round of the RFQ process, used only when cancelled (default 0).
 * @returns A list of navigation items with appropriate statuses.
 */
export function getNavItems(baseUrl: string, statuses: SuccessResponseBody<'/rfq/project/details/{projectId}'>['statuses'] = [], round: number = 0): NavItem[] {
  const defaultNavItems: Array<NavItem & {
    meta?: {
      round: number
      statuses: Partial<Record<MarketMakerRequestStatusEnumType, NavItemStatus>>
    }
  }> = [
    { label: RFQ_NAV_ITEM, link: `${baseUrl}/request-for-quotations`, status: 'available', meta: { round: 0, statuses: { new: 'completed' } } },
    {
      label: ROUND_1_NAV_ITEM,
      link: `${baseUrl}/received-quotes`,
      status: 'lock',
      meta: {
        round: 1,
        statuses: {
          round_one_quotes_in_review: 'available',
          round_one_waiting_for_quotes: 'available',
          round_one_quotes_accepted: 'completed',
          round_two_quotes_in_review: 'requoted',
          round_two_waiting_for_quotes: 'requoted',
        },
      },
    },
    {
      label: ROUND_2_NAV_ITEM,
      link: `${baseUrl}/received-requotes`,
      status: 'lock',
      meta: {
        round: 2,
        statuses: {
          round_two_quotes_in_review: 'available',
          round_two_waiting_for_quotes: 'available',
          round_two_quotes_accepted: 'completed',
        },
      },
    },
  ]

  const isRfqCancelled = statuses.some(s => s.status === 'cancelled')
  if (isRfqCancelled) {
    const { meta, ...cancelledNavItem } = defaultNavItems.find(item => item.meta?.round === round)!
    return [{ ...cancelledNavItem, status: 'cancelled' }]
  }

  return defaultNavItems.map((item) => {
    const { meta, ...navItem } = item
    const statusMap = item.meta?.statuses ?? {}
    const matched = statuses.find(s => statusMap[s.status])
    return {
      ...navItem,
      status: matched ? statusMap[matched.status]! : item.status,
    }
  })
}
